import {
  OutboundReferenceCollection,
  PurchaseOrderSectionConfig,
  PurchaseOrderSectionConfigDoc,
} from '@gooduncles/gu-app-schema';
import { Form, Input, InputNumber, Modal, Select, message } from 'antd';
import { FC, useEffect, useState } from 'react';

import { errorObjectToString } from 'src/utils/common';
import {
  createPurchaseOrderSectionConfig,
  updatePurchaseOrderSectionConfig,
} from 'src/utils/firebase/firebase-short-cut';

import useSWRProductSuppliers from 'src/hooks/swr/useSWRProductSuppliers';

const { TextArea } = Input;

/** OutboundReferenceCollection 옵션 */
const referenceCollectionOptions = [
  { label: '주문', value: OutboundReferenceCollection.ORDER },
  { label: '이슈', value: OutboundReferenceCollection.ISSUE_TASK },
];

interface FormValues {
  supplierId: string;
  referenceCollection: OutboundReferenceCollection;
  sectionHeader: string;
  sortOrder: number;
  description: string | null;
}

interface PurchaseOrderSectionConfigModalProps {
  open: boolean;
  onClose: () => void;
  /** 수정 모드일 때 기존 데이터 전달 */
  initialData?: PurchaseOrderSectionConfigDoc;
}

const PurchaseOrderSectionConfigModal: FC<PurchaseOrderSectionConfigModalProps> = ({ open, onClose, initialData }) => {
  const [form] = Form.useForm<FormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const { productSuppliers, isLoading: isSuppliersLoading } = useSWRProductSuppliers([['deletedAt', '==', null]]);

  const isEditMode = !!initialData;

  const supplierOptions = productSuppliers
    ?.sort((a, b) => a.name.localeCompare(b.name))
    ?.map((supplier) => ({
      label: supplier.name,
      value: supplier._id,
    }));

  useEffect(() => {
    if (open && initialData) {
      form.setFieldsValue({
        supplierId: initialData.supplierId,
        referenceCollection: initialData.referenceCollection,
        sectionHeader: initialData.sectionHeader,
        sortOrder: initialData.sortOrder,
        description: initialData.description,
      });
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        sortOrder: 1,
      });
    }
  }, [open, initialData, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = () => {
    form.submit();
  };

  const onFinish = async (values: FormValues) => {
    try {
      setIsLoading(true);

      if (isEditMode && initialData) {
        // 수정 모드
        const updateData: Partial<PurchaseOrderSectionConfig> = {
          supplierId: values.supplierId,
          referenceCollection: values.referenceCollection,
          sectionHeader: values.sectionHeader,
          sortOrder: values.sortOrder,
          description: values.description || null,
        };
        await updatePurchaseOrderSectionConfig(initialData._id, updateData);
        message.success('선주문 상품 설정이 수정되었습니다.');
      } else {
        // 생성 모드
        const newConfig: PurchaseOrderSectionConfig = {
          supplierId: values.supplierId,
          referenceCollection: values.referenceCollection,
          inventoryIds: [], // 다음 작업에서 구현
          sectionHeader: values.sectionHeader,
          sortOrder: values.sortOrder,
          description: values.description || null,
          enabled: false, // 기본값 false
        };
        await createPurchaseOrderSectionConfig(newConfig);
        message.success('선주문 상품 설정이 생성되었습니다.');
      }

      handleCancel();
    } catch (error) {
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={isEditMode ? '선주문 상품 설정 수정' : '선주문 상품 설정 추가'}
      open={open}
      centered
      onCancel={handleCancel}
      onOk={handleOk}
      okText={isEditMode ? '수정' : '추가'}
      cancelText='취소'
      width={600}
      confirmLoading={isLoading}
      okButtonProps={{ loading: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Form.Item
          name='supplierId'
          label='적용 대상 매입처'
          rules={[{ required: true, message: '매입처를 선택해주세요' }]}>
          <Select
            showSearch
            placeholder='매입처 선택'
            options={supplierOptions}
            loading={isSuppliersLoading}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <Form.Item
          name='referenceCollection'
          label='적용 대상 출처 컬렉션'
          rules={[{ required: true, message: '출처 컬렉션을 선택해주세요' }]}>
          <Select placeholder='컬렉션 선택' options={referenceCollectionOptions} />
        </Form.Item>

        <Form.Item
          name='sectionHeader'
          label='섹션 헤더 메시지'
          rules={[{ required: true, message: '헤더 메시지를 입력해주세요' }]}
          extra='발주서에서 분리된 섹션 상단에 표시될 안내 문구'>
          <Input placeholder='예: <선주문 상품입니다 / 내일 주세요>' />
        </Form.Item>

        <Form.Item
          name='sortOrder'
          label='정렬 순서'
          rules={[{ required: true, message: '정렬 순서를 입력해주세요' }]}
          extra='숫자가 클수록 하단에 배치됩니다. (기본 섹션은 0)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name='description' label='설명 (관리용)'>
          <TextArea rows={3} placeholder='설정에 대한 설명을 입력하세요' />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PurchaseOrderSectionConfigModal;
